/*
 * Framework-free popup trigger engine for embedded forms.
 *
 * `createPopupTrigger` returns a small controller that fires a single callback
 * when one of the configured triggers hits and the frequency cap allows it.
 * Supported triggers: exit-intent, a delay timer, a scroll-depth threshold, a
 * delegated click on a selector, and element visibility (IntersectionObserver).
 * Multiple triggers can be armed at once; the first to fire wins (OR) and the
 * rest are torn down. A composite `all` trigger fires only once every listed
 * sub-trigger has fired (AND). An arming guard (`MIN_TIME_ON_PAGE_MS`) holds
 * back the triggers that infer what the visitor is doing until they have been
 * on the page long enough for the inference to mean anything.
 * State (last shown / converted) is persisted to `localStorage`
 * keyed by form id, and degrades to an in-memory store when storage is blocked
 * or throwing. No preact/react imports — this ships inside the standalone embed
 * bundle.
 *
 * Arming consults the host page's tracking-consent signal first (`./trackingConsent`)
 * and records the answer on the controller as `consent()`. It gates the counting
 * layer, never this one: what a denial changes is whether an outcome is reported,
 * and the popup opens either way. Decision 0.2 of docs/consent-management_plan.md
 * and CLAUDE.md §13 both turn on that split.
 *
 * A visitor who changes that answer to no part-way through is the other half,
 * the CCPA one: `revokeTracking()` tears every listener and timer down and
 * denies every later ask, and the engine watches the configured signal so it
 * happens without the host page having to tell us.
 */

import {
  armNativeExitSignals,
  COMPUTER_EXIT_SIGNALS,
  createActiveTimeClock,
  DEFAULT_MIN_TIME_ON_PAGE_MS,
  DEFAULT_MOBILE_BREAKPOINT_PX,
  TOUCH_EXIT_SIGNALS,
  type ActiveTimeClock,
} from "./exitSignalsNative";
import {
  resolveTrackingConsent,
  TRACKING_CONSENT_REASONS,
  type TrackingConsentConfig,
  type TrackingConsentEnvironment,
  type TrackingConsentResolution,
  hasTrackingConsentSource,
  type TrackingConsentSource,
} from "./trackingConsent";

export type PopupTriggerType =
  | "exit"
  | "delay"
  | "scroll"
  | "click"
  | "visible"
  | "inactivity"
  | "intent"
  | "all";

/** Minimum intent tier the `intent` trigger fires on. */
export type PopupIntentTier = "warm" | "hot";

/**
 * Which visitors the popup opens for. Mirrors `FormPopupSettings["devices"]`
 * in `src/lib/types.ts`, which the embed reads off the stored settings; the
 * literal is repeated rather than imported because this engine ships in the
 * standalone embed bundle and stays free of `@/lib` imports.
 */
export type PopupDeviceTarget = "all" | "desktop" | "mobile";

/**
 * Which pages the popup opens on, matched on the path. Mirrors
 * `FormPopupSettings["pages"]` in `src/lib/types.ts`, repeated rather than
 * imported for the reason above: this engine ships in the standalone embed
 * bundle and stays free of `@/lib` imports.
 */
export type PopupPageTarget = {
  /** Patterns the popup opens on. Empty or absent = every page. */
  include?: string[];
  /** Patterns it never opens on, applied after `include`. */
  exclude?: string[];
};

/**
 * Who the popup is for, beyond `devices`/`pages` above. Mirrors
 * `FormPopupSettings["targeting"]` in `src/lib/types.ts`, repeated for the
 * reason above: this engine ships in the standalone embed bundle and stays
 * free of `@/lib` imports. Every configured rule must pass
 * (`docs/popup-formats-and-targeting_plan.md` task 3.1).
 */
export type PopupTargetingRules = {
  /** Matches when the visitor's landing URL carried ANY of these pairs. */
  utm?: { key: string; value: string }[];
  /** Matches when the visitor's landing referrer host is one of these. */
  referrerHosts?: string[];
  /** Matches new or returning visitors to this site (a localStorage mark). */
  visitor?: "new" | "returning";
  /** Matches inside this date/day/time window, evaluated in its own zone. */
  schedule?: {
    start?: string;
    end?: string;
    weekdays?: number[];
    timezone: string;
  };
  /**
   * Matches one of these ISO 3166-1 alpha-2 codes. The engine has no way to
   * read a visitor's country on its own (there is no client-side signal to
   * read, unlike device or page); it only compares against whatever
   * `visitorCountry` the caller supplies. A caller that never sets it (every
   * one today; task 3.2 wires the embed to pass the value the server's edge
   * header resolved) means this rule can never pass, which is the same
   * "unknown fails a country rule" answer a known visitor outside the list
   * gets, not a special case.
   */
  countries?: string[];
};

/**
 * Longest page pattern accepted. Well past any real path, and a cap is what
 * keeps an owner-authored string from becoming an unbounded regex compiled in
 * every visitor's browser. Oversized is dropped whole rather than truncated: a
 * cut path is a different page, and narrowing a popup onto one nobody named is
 * worse than ignoring the rule.
 *
 * The server normalizer imports this rather than restating it, so a pattern the
 * write path stores is one this engine will compile.
 */
export const MAX_POPUP_PAGE_PATTERN_LENGTH = 200;

/**
 * Most patterns one list may hold. A page rule is something a person types into
 * a dashboard; twenty is past any rule somebody is maintaining by hand, and the
 * cap stops a hand-written payload from putting an unbounded array through the
 * matcher on every visit.
 */
export const MAX_POPUP_PAGE_PATTERNS = 20;

/** Normalized trigger descriptor the engine arms internally. */
export type PopupTriggerDescriptor =
  | { type: "exit" }
  | { type: "delay"; ms?: number }
  | { type: "scroll"; percent: number }
  | { type: "click"; selector: string }
  | { type: "visible"; selector: string }
  | { type: "inactivity"; ms?: number }
  | { type: "intent"; tier?: PopupIntentTier }
  // Composite AND: fires only once EVERY sub-trigger has been satisfied. Each sub
  // latches independently, so `["intent:warm","exit"]` opens on the exit that
  // follows a session already scored warm (engaged-then-leaving), not on either
  // alone. Order-independent; whichever completes the set fires it.
  | { type: "all"; of: PopupTriggerDescriptor[] };

/**
 * Accepted trigger inputs. A bare type (`"exit"`), a string form
 * (`"delay:8000"`, `"scroll:50"`, `"click:#cta"`, `"visible:#pricing"`,
 * `"inactivity:30000"`, `"intent:hot"`), a descriptor object, or a list of any
 * of those (first to fire wins, i.e. OR). Join two forms with `&`
 * (`"intent:warm&exit"`) or pass `{type:"all", of:[...]}` for AND: the popup
 * opens only once every listed trigger has fired.
 */
export type PopupTriggerInput =
  | PopupTriggerType
  | string
  | PopupTriggerDescriptor
  | Array<PopupTriggerType | string | PopupTriggerDescriptor>;

export interface PopupTriggerOptions {
  /** Which trigger(s) to arm. Unknown/invalid entries are dropped. */
  trigger: PopupTriggerInput;
  /** Delay in ms for the `delay` trigger. */
  delayMs?: number;
  /** Re-show cap in days after a show/convert. 0 = per session (in-memory). */
  frequencyDays?: number;
  /**
   * Total number of times this visitor may ever see the popup (absent = no
   * limit, 0 = never). Independent of `frequencyDays`, and the stricter of the
   * two wins: the count is consulted even when `frequencyDays` is 0
   * (per-session), and waiting out the re-show window does not return shows the
   * cap has already spent. Counted in persisted state, so it survives a reload.
   */
  maxShows?: number;
  /**
   * Days to suppress re-show after an explicit decline (default 90). Distinct
   * from `frequencyDays`: a decline is a stronger signal than a passive close,
   * so it holds even when `frequencyDays` is 0 (per-session). 0 = no decline
   * suppression window.
   */
  declineSuppressDays?: number;
  /**
   * Which devices the popup opens on (default "all"). Checked once, when `arm`
   * runs: a visitor the popup is not meant for gets no listeners and no timers,
   * rather than an armed trigger that declines to fire.
   */
  devices?: PopupDeviceTarget;
  /**
   * What a touch visitor gets instead of `trigger` above (task 3.1 of
   * docs/popup-triggers-and-funnel_plan.md). Read live, when `arm` runs,
   * through the same device check `isTouchDevice` uses below: a touch visitor
   * arms this instead of `trigger`, a non-touch visitor arms `trigger`
   * unchanged. Absent means no override, which is what every form stored
   * before this field existed gets, so nothing here changes what a desktop
   * visitor or a form with no `mobileTrigger` sees. An override that parses to
   * no usable descriptor (a bad value, an empty list) arms nothing on a touch
   * device rather than falling back to `trigger`, the same way an unparseable
   * `trigger` already arms nothing: a trigger a visitor cannot meet is not
   * the same as one that was never set.
   */
  mobileTrigger?: PopupTriggerInput;
  /**
   * Which pages the popup opens on (absent = every page). Compiled once, when
   * `createPopupTrigger` runs, and checked once, when `arm` runs. A popup with
   * no rule never reads `location` at all, which is what keeps this free for
   * the forms that predate it.
   */
  pages?: PopupPageTarget;
  /**
   * Who the popup is for, beyond `devices`/`pages`: campaign, referrer,
   * new/returning, a schedule, a country list. Checked once, when `arm` runs,
   * beside the two rules above; a popup with no rule reads nothing it would
   * not have read anyway. See `PopupTargetingRules`.
   */
  targeting?: PopupTargetingRules;
  /**
   * The visitor's country, resolved server-side (there is no reliable
   * client-side signal) and handed in by the caller. Absent means unknown,
   * which fails a `targeting.countries` rule the same way a known country
   * outside the list does — see the note on `PopupTargetingRules.countries`.
   */
  visitorCountry?: string;
  /** Form id used to key persisted state. */
  formId?: string;
  /**
   * How long the visitor must have been on the page before an inferred signal
   * counts (default 15000, 0 disables). See `MIN_TIME_ON_PAGE_MS`.
   */
  minTimeOnPageMs?: number;
  /**
   * Top-edge band in px within which a cursor leaving the window counts as exit
   * intent (default 20, capped at the viewport height). 0 = the top edge
   * exactly. Only the `exit` trigger reads it.
   */
  exitSensitivityPx?: number;
  /**
   * How long `exit` waits after the cursor leaves before counting it, in ms
   * (default 1000). Cancelled if the cursor comes back, so 0 removes the second
   * chance a visitor gets to change their mind.
   */
  mouseLeaveDelayMs?: number;
  /**
   * Upward travel that counts as `exit`, in px, by device class (default 200
   * mobile / 400 desktop). Covered inside `scrollUpWindowMs`, so the pair
   * describes a speed rather than a distance.
   */
  scrollUpThreshold?: { mobile: number; desktop: number };
  /**
   * Viewport width at or below which the mobile scroll threshold applies, in px
   * (default 768).
   */
  mobileBreakpoint?: number;
  /**
   * Window the upward travel has to happen inside, in ms (default 500). 0 turns
   * the fast-scroll signal off and leaves the other exit signals armed.
   */
  scrollUpWindowMs?: number;
  /** Storage override. Defaults to `window.localStorage` when available. */
  storage?: StorageLike;
  /**
   * Where to read the host page's tracking-consent signal, by name: a value on
   * `window`, a cookie, or a `localStorage` key. See `./trackingConsent`, which
   * owns the reading and names no vendor. Absent means the customer configured
   * none, which is most sites and resolves to the default posture.
   *
   * The engine consults this before it attaches anything (CLAUDE.md §13), and
   * the answer is available on the controller for as long as it lives. What the
   * answer governs is one layer, per decision 0.2 of
   * docs/consent-management_plan.md: see `consent()` on `PopupTrigger`.
   */
  consent?: TrackingConsentConfig;
  /**
   * Read sources for the consent lookup. Defaults to the ambient page.
   * Injectable so a test can drive a signal without touching real cookies,
   * storage or `window`.
   */
  consentEnvironment?: TrackingConsentEnvironment;
  /**
   * How often the configured consent signal is re-read, in ms (default 1000).
   * 0 turns the watch off, which leaves `revokeTracking()` as the only way a
   * withdrawal is honoured. Nothing is polled when no consent source is
   * configured, or when the signal already said no.
   */
  consentPollMs?: number;
  /**
   * Called once, when tracking is revoked mid-session. The embed logs the
   * resolution's `reason` under `data-kiroku-debug` with it; a host page can
   * use it to know its "do not sell" control took effect.
   */
  onTrackingRevoked?: (resolution: TrackingConsentResolution) => void;
}

export interface PopupTrigger {
  /**
   * Attach listeners/timers; `onFire` runs at most once if the cap allows.
   * Called with `true` when the descriptor that fired is user-initiated
   * (`click` at the top level; see `isUserInitiated`), so a caller can tell
   * a visitor's own press apart from every other, uninvited trigger without
   * re-reading its own trigger config (docs/popup-offer-screen_plan.md 3.2).
   *
   * `reason` (task 4.1, docs/popup-triggers-and-funnel_plan.md) says which
   * signal actually fired, for the `popup_shown` beacon's own `reason` field:
   * `mouse_top`, `blur`, `visibility`, `flick` or `keyboard` for the five
   * `exit` sub-signals (`NativeExitReason` in `./exitSignalsNative`), or the
   * top-level trigger type itself (`delay`, `scroll`, `click`, `visible`,
   * `inactivity`, `intent`) for everything else. A composite `all` reports
   * `"all"`, not whichever half completed the pair, because the thing an
   * owner needs to know is that it took both.
   */
  arm(onFire: (userInitiated: boolean, reason: string) => void): void;
  /** Record that the popup was shown (starts the frequency window). */
  markShown(): void;
  /** Record a successful submission (starts the frequency window). */
  markConverted(): void;
  /** Record an explicit decline (starts the longer suppress window). */
  markDeclined(): void;
  /** Remove all listeners/timers without firing. */
  disarm(): void;
  /**
   * Stop tracking this visitor now, and stay stopped. The CCPA/CPRA path: an
   * opt-out has to take effect in the session it was made in, which is why
   * `exitSignalsNative.ts` has always returned a teardown.
   *
   * Called by the engine itself when the signal it was given changes its answer
   * to no, which is the path a customer gets by naming their consent source in
   * the dashboard. It is on the controller as well so a caller holding one (the
   * embed, the marketing island) can withdraw on its own account; the embed
   * exposes no host-page global for it, because nothing has asked for one and a
   * "do not sell or share" control that only works from JavaScript would be a
   * developer escape hatch standing in for the dashboard path (CLAUDE.md §0a).
   *
   * Every armed listener and timer goes, through the same teardown a fire runs,
   * and every later `consent()` reports a denial, so nothing further is observed
   * and nothing further is counted. A second call is a no-op.
   *
   * What it deliberately does not do is close a modal already on screen or drop
   * a part-typed submission. The visitor asked for that form, it is not
   * analytics, and taking it away would punish them for answering a banner.
   */
  revokeTracking(): TrackingConsentResolution;
  /**
   * What the host page's consent signal said, read once when `arm` ran and
   * before a single listener was attached, or the denial `revokeTracking()`
   * replaced it with. On a controller that never armed, reading this consults
   * the signal on the spot.
   *
   * Whoever counts an outcome asks this first: `allowed` is the answer, and
   * `reason` is a sentence written for the form owner, which is what the
   * dashboard needs when it has to say an event was not counted and why.
   *
   * It does not say whether the popup opens. Decision 0.2 of
   * docs/consent-management_plan.md splits the popup into three layers and
   * gates one of them. Opening the modal is never gated, because a page
   * reacting to a pointer stores nothing and sends nothing, and because a
   * customer paying for a popup that silently stops appearing for part of their
   * audience is the CLAUDE.md §13 trap. The `kiroku:popup:*` frequency cap is
   * never gated either: it exists to show people fewer popups and it never
   * leaves the device. Counting the outcome is the gated layer, and it is the
   * only one that transmits anything.
   */
  consent(): TrackingConsentResolution;
}

/** Minimal subset of the Web Storage API we depend on. */
export interface StorageLike {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
}

interface PopupState {
  shownAt?: number;
  convertedAt?: number;
  declinedAt?: number;
  /**
   * How many times the popup has opened for this visitor, ever. Distinct from
   * `shownAt`, which only records the most recent one and so cannot answer
   * "how many times in total". Written by `markShown` alone: a conversion and a
   * decline each open their own suppression window, and counting them too would
   * spend the cap twice for one appearance.
   */
  shownCount?: number;
}

/**
 * How long `delay` waits, in active time, when the caller names no length of
 * its own. The one place this number is written: the server normalizer and the
 * settings panel both read it from here, so a form saved in the dashboard and
 * the same form created through the API open at the same moment. It was written
 * three times, and the same triplication for the re-show window was ledger 8,
 * where the two copies had already drifted apart.
 *
 * 15 seconds since 2026-10-29, up from 8. An 8 second timer opens before a
 * visitor has read the headline, and our own rule keeps a phone popup at or
 * above 10 seconds (`MOBILE_DELAY_FLOOR_MS`). 15 matches the active-time guard
 * the exit, scroll and intent triggers already wait out and
 * `DEFAULT_MOBILE_DELAY_MS`, so a computer and a phone now agree. A form that
 * stores no `delayMs` opens later than it did, never earlier.
 */
export const DEFAULT_DELAY_MS = 15000;
/**
 * How long `inactivity` counts when the caller names no length of its own.
 * Split from `DEFAULT_DELAY_MS` on 2026-10-29: eight seconds of stillness is a
 * pause worth noticing (the research ladder is 8, 20 and 40 s,
 * docs/popup-experiment-factors_research.md §1), where eight seconds of
 * activity is not. Same single-source rule as the number above, enforced by
 * tests/guard/popup-defaults-agree.test.ts.
 */
export const DEFAULT_INACTIVITY_MS = 8000;
/**
 * The default length for a stored timer, by trigger: `inactivity` counts a
 * pause, every other trigger that reads `delayMs` (`delay`, and the mobile
 * fallback) waits out a duration. The panel and the server normalizer ask here
 * so the one stored `delayMs` field never seeds the wrong number.
 */
export function defaultTimerMs(trigger: string | undefined): number {
  return trigger === "inactivity" ? DEFAULT_INACTIVITY_MS : DEFAULT_DELAY_MS;
}
/**
 * What `delay`/`inactivity` default to on a touch device when nothing is
 * stored, in ms (task 2.2). Our own rule is no phone popup under 10 seconds
 * (docs/popup-experiment-factors_research.md §1); 15 gives a phone visitor
 * a few more seconds than a computer gets by default, same reasoning as
 * `DEFAULT_SCROLL_UP_THRESHOLD_PX` picking a different number per device
 * class in exitSignalsNative.ts.
 */
export const DEFAULT_MOBILE_DELAY_MS = 15000;
/**
 * Floor for `delay`/`inactivity` on a touch device, in ms (task 2.2):
 * whatever is stored, including a value an owner tuned on a computer,
 * never opens faster than this on a phone or tablet. `exit` already has an
 * equivalent (`DEFAULT_MIN_TIME_ON_PAGE_MS` in exitSignalsNative.ts);
 * `delay`/`inactivity` are owner-set numbers with no floor until now.
 */
export const MOBILE_DELAY_FLOOR_MS = 10000;
/**
 * Interaction events that reset the `inactivity` idle timer. All are bound
 * passively on `document` in the capture phase. Covers the set use-exit-intent
 * resets on (click, keydown, mousedown, mousemove, scroll, touchstart; research
 * §9 cross-library sweep) plus `pointerdown` and `wheel`.
 *
 * `click` is not redundant with `mousedown`: an activation that never goes
 * through the pointer still arrives as a click, so a button pressed via the
 * keyboard, assistive tech, or another script's `el.click()` counts as the
 * visitor being present.
 *
 * The capture phase is what makes `scroll` cover the whole page. A scroll
 * inside an overflow container is fired at that element with `bubbles: false`,
 * so a listener bound for the bubble phase never sees it, and a visitor reading
 * their way down a scrollable panel looked idle. The reference binds these on
 * `window` with capture for the same reason.
 *
 * `scroll` here *resets* the idle window, the opposite role it plays in the
 * `scroll` trigger (which fires on scroll depth); the two triggers never share
 * a handler.
 */
const INACTIVITY_EVENTS = [
  "mousemove",
  "mousedown",
  "click",
  "keydown",
  "scroll",
  "touchstart",
  "pointerdown",
  "wheel",
] as const;
/**
 * How long the same visitor waits before the popup opens again when the form
 * stores no window of its own. The one place this number is written: the
 * settings panel and the server-side normalizer both read it from here, and
 * tests/guard/popup-defaults-agree.test.ts fails when a second copy appears.
 *
 * It was 7 here while both of those wrote 30, so a form built in the dashboard
 * waited a month and the same form created through the API or MCP, which sends
 * no `frequencyDays`, waited a week (ledger 8). 30 is the value kept: the
 * visitor who did not ask to see the popup waits longer rather than shorter,
 * which is the direction a default nobody configured should err in.
 */
export const DEFAULT_FREQUENCY_DAYS = 30;
const DEFAULT_DECLINE_SUPPRESS_DAYS = 90;
/**
 * How long a visitor who submitted the form goes without being asked again,
 * whatever the frequency setting says. Longer than the decline window: somebody
 * who said yes has less reason to be asked than somebody who said no. Ben's
 * decision, 2026-09-24 (docs/popup-experiment-factors_research.md §2); before
 * it a submission counted towards `frequencyDays` (30), so converters were
 * re-prompted sooner than decliners. A click-to-open still opens: that path
 * skips suppression because the visitor asked.
 */
export const CONVERTED_SUPPRESS_DAYS = 365;
/**
 * How often the configured consent signal is re-read while the popup is live,
 * in ms.
 *
 * CCPA/CPRA is opt-out: a visitor who turns tracking off has to be honoured in
 * the session they are in, not on their next page load, so something has to
 * notice. Polling rather than a listener because every consent tool announces a
 * change differently, and subscribing to one by name would put a vendor inside
 * the engine, which the named-source interface in `./trackingConsent` exists to
 * keep out.
 *
 * A second is close enough to immediate for a person clicking a banner, and the
 * work per tick is reading one cookie or one property. It costs one interval,
 * and only on the sites that named a signal in the first place: a form with no
 * consent source configured starts no timer at all.
 */
export const DEFAULT_CONSENT_POLL_MS = 1000;
/**
 * What a form owner is told about a visitor who withdrew consent part-way
 * through. One plain sentence, the same job the other sentences in
 * `./trackingConsent` do: CLAUDE.md §13 requires that a suppressed event says
 * why, in words that mean something to somebody who did not write the engine.
 * It lives over there with its siblings so the dashboard panel task 5.3 added
 * can print it without importing the whole engine.
 */
const TRACKING_REVOKED_REASON = TRACKING_CONSENT_REASONS.revoked;
/**
 * Nothing the engine infers about the visitor counts until they have been on
 * the page this long. It is a guard, not a trigger: when it elapses the popup
 * does not open, the trigger's own signal still has to arrive. exit-intent-js
 * has this backwards (`timeOnPage` opens the popup on the timer), which is the
 * correction research §9 records as decisive against adopting it.
 *
 * Read off the signals module rather than restated, so the length this engine
 * hands to `armExit` and the length that module falls back to for a host arming
 * it directly are the same number by construction. Both files wrote 15000
 * before, and a drift there is invisible: every engine-armed popup would keep
 * its 15s while a direct caller silently got another length.
 */
export const MIN_TIME_ON_PAGE_MS = DEFAULT_MIN_TIME_ON_PAGE_MS;
/**
 * Trigger types this engine holds back until the guard elapses. `exit` is
 * guarded too, inside `exitSignalsNative.ts`, which drops an early signal
 * rather than attaching late: a departure gesture is momentary, so replaying it
 * 14 seconds later would open the popup at a moment the visitor did nothing.
 *
 * The three that are absent say when to open rather than infer it. `delay` and
 * `inactivity` open at a time the owner typed into the dashboard, where the
 * field takes any value from 0 up, so a 15s floor would silently overrule it.
 * `click` opens because the visitor pressed the element: the homepage "Talk to
 * us" button is a `click:` popup (src/pages/index.astro), and guarding it would
 * leave that button dead for the first 15 seconds. `all` is absent because it
 * arms its halves back through this same dispatch, so each half is held or not
 * on its own.
 */
const HELD_UNTIL_GUARD_ELAPSES: ReadonlySet<PopupTriggerType> = new Set([
  "scroll",
  "visible",
  "intent",
]);
/**
 * Every trigger the guard can delay, for anything that has to tell a form owner
 * when their popup will not open yet. `exit` joins the three above because
 * `armExit` hands the same length to `exitSignalsNative.ts`, which drops a
 * departure signal that arrives inside it.
 *
 * `all` is absent for the reason given above: each half is armed back through
 * this dispatch, so a pair is delayed when one of its halves is, and a reader
 * has to look at the halves. The dashboard panel imports this so its "nothing
 * opens for the first N seconds" line covers the same triggers the engine
 * actually holds (`tests/components/form-settings-popup.test.tsx`).
 */
export const GUARDED_BY_MIN_TIME_ON_PAGE: ReadonlySet<PopupTriggerType> =
  new Set(["exit", ...HELD_UNTIL_GUARD_ELAPSES]);
const MAX_TIMEOUT_MS = 2147483647; // 32-bit signed max; larger wraps to fire-now
const DAY_MS = 24 * 60 * 60 * 1000;
const KEY_PREFIX = "kiroku:popup:";
/** Key prefix for the `frequencyDays === 0` per-session show mark (task 1.2). */
const SESSION_KEY_PREFIX = "kiroku:popup:session:";

/**
 * VeritaMetrics dispatches this DOM CustomEvent on `document` when a session
 * reaches a warm/hot intent tier (opt-in, cookieless; the site adds
 * `data-warm-lead="true"` to its VeritaMetrics tag). The `intent` trigger listens
 * for it; the detail is `{ tier, score, reasons }` and carries no personal data.
 *
 * Exported so `kiroku-embed.ts` can listen for the identical event to keep the
 * latest tier for submission metadata (plan 3.5 of
 * docs/guided-shopping_plan.md), without a second, drifting copy of the name.
 */
export const VM_INTENT_EVENT = "veritametrics:intent";
/** Ordered tiers so `intent`/`intent:hot` can gate on a minimum. */
const TIER_RANK: Record<string, number> = { cold: 0, warm: 1, hot: 2 };

/** Coerce to a finite number in [min, fallback-safe]; else return fallback. */
function toDelay(
  value: number | undefined,
  fallback: number = DEFAULT_DELAY_MS,
): number {
  if (typeof value !== "number" || !Number.isFinite(value) || value <= 0) {
    return fallback;
  }
  return Math.min(value, MAX_TIMEOUT_MS);
}

/**
 * The ms a `delay`/`inactivity` timer actually waits (task 2.2). A touch
 * device defaults to `DEFAULT_MOBILE_DELAY_MS` rather than the computer default
 * when nothing is stored, and never resolves under `MOBILE_DELAY_FLOOR_MS`
 * whatever is stored: an owner's computer-tuned 3-second delay still opens at
 * 10 seconds on a phone. A non-touch device is untouched, exactly as before
 * this task.
 */
function resolveTimerMs(
  raw: number | undefined,
  touch: boolean,
  desktopDefault: number = DEFAULT_DELAY_MS,
): number {
  const ms = toDelay(raw, touch ? DEFAULT_MOBILE_DELAY_MS : desktopDefault);
  return touch ? Math.max(ms, MOBILE_DELAY_FLOOR_MS) : ms;
}

/** Coerce frequency days: finite and >= 0 (0 is meaningful: per session). */
function toFrequencyDays(value: number | undefined): number {
  if (typeof value !== "number" || !Number.isFinite(value) || value < 0) {
    return DEFAULT_FREQUENCY_DAYS;
  }
  return value;
}

/** Coerce the arming guard: finite and >= 0 (0 = no guard). */
function toMinTimeOnPage(value: number | undefined): number {
  if (typeof value !== "number" || !Number.isFinite(value) || value < 0) {
    return MIN_TIME_ON_PAGE_MS;
  }
  return Math.min(value, MAX_TIMEOUT_MS);
}

/**
 * Coerce the show-count cap to a whole number of shows, or `null` for no cap.
 *
 * Everything unusable reads as no cap rather than as a low one: a negative or
 * non-finite value coming off a hand-written payload must not read as "already
 * over the limit" and silently hide a popup the owner switched on, which they
 * would have no way to diagnose. 0 is kept, since an explicit 0 is the owner
 * saying never.
 */
function toMaxShows(value: number | undefined): number | null {
  if (typeof value !== "number" || !Number.isFinite(value) || value < 0) {
    return null;
  }
  return Math.floor(value);
}

/** Read a stored show count, treating anything unusable as none seen yet. */
function toShownCount(value: unknown): number {
  if (typeof value !== "number" || !Number.isFinite(value) || value < 0) {
    return 0;
  }
  return Math.floor(value);
}

/** Coerce decline-suppress days: finite and >= 0 (0 = no suppression). */
function toDeclineSuppressDays(value: number | undefined): number {
  if (typeof value !== "number" || !Number.isFinite(value) || value < 0) {
    return DEFAULT_DECLINE_SUPPRESS_DAYS;
  }
  return value;
}

/**
 * Coerce the consent poll interval: finite and >= 0, else the default. 0 is
 * kept rather than treated as absent, because it is the way a caller says "do
 * not poll this page", leaving `revokeTracking()` as the only withdrawal path.
 */
function toConsentPollMs(value: number | undefined): number {
  if (typeof value !== "number" || !Number.isFinite(value) || value < 0) {
    return DEFAULT_CONSENT_POLL_MS;
  }
  return value;
}

/**
 * Whether the customer named anywhere to read a consent answer from. No source
 * means no consent tool to change its mind, so there is nothing to watch and no
 * timer runs: that is most sites, and they behave exactly as they did before.
 *
 * The set of sources lives in `trackingConsent.ts` rather than here. This file
 * had its own copy of the list, so a source added to the reader was gated at
 * arm time and then never watched for a withdrawal.
 */
function hasConsentSource(config: TrackingConsentConfig | undefined): boolean {
  return hasTrackingConsentSource(config);
}

/**
 * Coerce the device target. Anything else, including a value stored by a
 * hand-written payload, means every device: the alternative is a popup the
 * owner switched on that silently opens for nobody.
 */
function toDeviceTarget(value: string | undefined): PopupDeviceTarget {
  return value === "desktop" || value === "mobile" ? value : "all";
}

/** Every character a regex engine reads as something other than itself. */
const REGEX_METACHARACTERS = /[.*+?^${}()|[\]\\]/g;

/**
 * Normalize one page pattern, or one real pathname, to the single shape both
 * sides of the match are held in. Returns `undefined` when nothing usable is
 * left, which the callers treat as "this entry says nothing" rather than as
 * "match nothing".
 *
 * The rules are decided once here so they are not rediscovered per call site
 * (docs/popup-url-targeting_plan.md, "Normalisation"): the query string and the
 * hash are discarded, matching is case-insensitive, and a trailing slash is
 * dropped, so a visitor arriving at `/Pricing/?utm_source=x#plans` is on
 * `/pricing`. A pattern with no leading `/` gets one, because a path is what
 * the owner was asked for and a pattern that can never match reads as a broken
 * popup rather than as a typo.
 */
export function normalizePopupPagePattern(value: unknown): string | undefined {
  if (typeof value !== "string") return undefined;
  const trimmed = value.trim();
  if (trimmed === "" || trimmed.length > MAX_POPUP_PAGE_PATTERN_LENGTH) {
    return undefined;
  }
  // Cut at whichever of `?` and `#` comes first: `/a#b?c` has no query string,
  // it has a fragment containing a question mark.
  const cut = trimmed.search(/[?#]/);
  let path = (cut === -1 ? trimmed : trimmed.slice(0, cut)).toLowerCase();
  if (path === "") return undefined;
  if (!path.startsWith("/") && !path.startsWith("*")) path = "/" + path;
  // Length-guarded so the root path stays `/` rather than becoming empty.
  if (path.length > 1 && path.endsWith("/")) path = path.slice(0, -1);
  return path === "" ? undefined : path;
}

/**
 * Compile a page pattern to an anchored matcher, or `null` when it is unusable.
 *
 * This is the whole safety property of page targeting: the owner's string is
 * split on `*`, every other character of every segment is escaped, and only
 * then is a `RegExp` built. Nothing an owner types can reach the regex engine
 * as syntax, so a pattern cannot become a catastrophic backtracker on a
 * customer's site (`tests/guard/popup-url-patterns-are-bounded.test.ts`), and
 * the anchors mean a pattern is never a substring match on the path.
 */
export function compilePopupPagePattern(pattern: string): RegExp | null {
  if (typeof pattern !== "string") return null;
  if (pattern === "" || pattern.length > MAX_POPUP_PAGE_PATTERN_LENGTH) {
    return null;
  }
  const source = pattern
    .split("*")
    .map((segment) => segment.replace(REGEX_METACHARACTERS, "\\$&"))
    .join(".*");
  try {
    return new RegExp(`^${source}$`);
  } catch {
    // Unreachable with everything escaped, and caught anyway: a throw here
    // would take down arming for a form whose other settings are fine.
    return null;
  }
}

/** Compile one list, normalizing each entry and capping how many are kept. */
function compilePageList(patterns: string[] | undefined): RegExp[] {
  if (!Array.isArray(patterns)) return [];
  const matchers: RegExp[] = [];
  for (const raw of patterns) {
    const normalized = normalizePopupPagePattern(raw);
    if (normalized === undefined) continue;
    const matcher = compilePopupPagePattern(normalized);
    if (matcher === null) continue;
    matchers.push(matcher);
    if (matchers.length === MAX_POPUP_PAGE_PATTERNS) break;
  }
  return matchers;
}

/**
 * Compile a page rule once, at create time, or `null` when there is no rule to
 * apply. `null` is the short-circuit the absent case depends on: it is what
 * every form stored before this field existed resolves to, and it is checked
 * before anything reads `location`.
 *
 * A rule whose every pattern was unusable also resolves to `null`. Narrowing to
 * nothing would silently switch off a popup the owner enabled, which is the one
 * outcome a repair here must not produce.
 */
function compilePageTarget(
  pages: PopupPageTarget | undefined,
): { include: RegExp[]; exclude: RegExp[] } | null {
  if (!pages || typeof pages !== "object") return null;
  const include = compilePageList(pages.include);
  const exclude = compilePageList(pages.exclude);
  if (include.length === 0 && exclude.length === 0) return null;
  return { include, exclude };
}

/**
 * The path this page is on, normalized the way patterns are, or `null` when
 * there is no readable `location` (a sandboxed frame, a non-DOM host). Only
 * called once a rule exists.
 */
function currentPathname(): string | null {
  try {
    const path = window.location?.pathname;
    if (typeof path !== "string") return null;
    return normalizePopupPagePattern(path) ?? "/";
  } catch {
    return null;
  }
}

/**
 * Where the visitor's landing UTM parameters and referrer host are cached for
 * the rest of the tab session, so a `utm`/`referrerHosts` rule on page three
 * still sees what page one arrived with. One key for the whole site, not per
 * form: two popup forms on two different pages of the same visit share one
 * landing moment.
 */
const LANDING_KEY = "kiroku:popup:landing";
/**
 * Site-wide (not per-form) localStorage mark: this browser has been seen
 * before. Written only once tracking consent has said yes, see `arm()`.
 */
const VISITOR_SEEN_KEY = "kiroku:popup:seen";

interface LandingContext {
  /** Lowercased `utm_*` param names to their (trimmed, lowercased) values. */
  utm: Record<string, string>;
  /** Lowercased referrer host, or "" for a direct visit (no referrer at all). */
  referrerHost: string;
}

/** Read the current URL's `utm_*` params and the referrer host, once. */
function readCurrentLandingContext(): LandingContext {
  const utm: Record<string, string> = {};
  try {
    const params = new URLSearchParams(window.location?.search ?? "");
    for (const [key, value] of params.entries()) {
      if (key.toLowerCase().startsWith("utm_")) {
        utm[key.toLowerCase()] = value.trim().toLowerCase();
      }
    }
  } catch {
    // No readable location: no campaign to record.
  }
  let referrerHost = "";
  try {
    referrerHost = document.referrer
      ? new URL(document.referrer).host.toLowerCase()
      : "";
  } catch {
    // An unparseable referrer is the same as none.
  }
  return { utm, referrerHost };
}

/**
 * The visitor's landing context for this tab session: read back what an
 * earlier page already captured, or capture it now if this is the first
 * popup on the first page to ask. Idempotent, so whichever page loads first
 * in the session wins and every later page reads the same answer, which is
 * what lets a `utm`/`referrerHosts` rule on page three still match a visitor
 * who arrived on page one carrying the campaign. Read unconditionally by
 * every armed popup, whether or not it declares a `utm`/`referrerHosts` rule
 * of its own: a later popup on a later page has no other way to learn what
 * page one arrived with.
 */
function landingContext(sessionStorage: SafeStorage): LandingContext {
  const raw = sessionStorage.read(LANDING_KEY);
  if (raw) {
    try {
      const parsed = JSON.parse(raw);
      if (parsed && typeof parsed === "object") {
        return {
          utm: parsed.utm && typeof parsed.utm === "object" ? parsed.utm : {},
          referrerHost:
            typeof parsed.referrerHost === "string" ? parsed.referrerHost : "",
        };
      }
    } catch {
      // A corrupted record is the same as none: fall through and recapture.
    }
  }
  const captured = readCurrentLandingContext();
  sessionStorage.write(LANDING_KEY, JSON.stringify(captured));
  return captured;
}

/** A visitor matches `targeting.utm` when the landing URL carried ANY pair. */
function targetsUtmRule(
  rules: PopupTargetingRules,
  landing: LandingContext,
): boolean {
  const utm = rules.utm;
  if (!utm || utm.length === 0) return true;
  return utm.some((rule) => landing.utm[rule.key] === rule.value);
}

/**
 * A direct visit (no referrer) fails a referrer rule rather than passing it:
 * there is nothing to have matched, and passing it would open the popup for
 * every visitor a referrer rule was meant to narrow away from.
 */
function targetsReferrerRule(
  rules: PopupTargetingRules,
  landing: LandingContext,
): boolean {
  const hosts = rules.referrerHosts;
  if (!hosts || hosts.length === 0) return true;
  if (!landing.referrerHost) return false;
  return hosts.includes(landing.referrerHost);
}

/** New/returning is read from a mark this same function's caller may write. */
function targetsVisitorRule(
  rules: PopupTargetingRules,
  alreadySeen: boolean,
): boolean {
  const wanted = rules.visitor;
  if (!wanted) return true;
  return wanted === "returning" ? alreadySeen : !alreadySeen;
}

/**
 * An unknown visitor country fails a `countries` rule rather than passing it,
 * the same direction a referrer rule takes on a direct visit: see
 * `PopupTargetingRules.countries` for why the engine so rarely knows one.
 */
function targetsCountryRule(
  rules: PopupTargetingRules,
  visitorCountry: string | undefined,
): boolean {
  const countries = rules.countries;
  if (!countries || countries.length === 0) return true;
  if (!visitorCountry) return false;
  return countries.includes(visitorCountry.toUpperCase());
}

const SCHEDULE_WEEKDAY_INDEX: Record<string, number> = {
  Sun: 0,
  Mon: 1,
  Tue: 2,
  Wed: 3,
  Thu: 4,
  Fri: 5,
  Sat: 6,
};

/**
 * "Now" as wall-clock components in `timezone`, in the same
 * `YYYY-MM-DDTHH:mm` shape `start`/`end` are stored in, plus the weekday.
 * `null` when the zone cannot be resolved, which should not happen for a zone
 * the normalizer already validated; a defensive read here costs nothing and a
 * throw must not take arming down with it.
 */
function nowInTimeZone(
  timezone: string,
): { minute: string; weekday: number } | null {
  try {
    const parts = new Intl.DateTimeFormat("en-US", {
      timeZone: timezone,
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      hour12: false,
      weekday: "short",
    }).formatToParts(new Date());
    const get = (type: string) =>
      parts.find((p) => p.type === type)?.value ?? "";
    let hour = get("hour");
    // Some locales name midnight "24" under hour12: false; normalize it back
    // to the "00" the stored boundaries use.
    if (hour === "24") hour = "00";
    const minute = `${get("year")}-${get("month")}-${get("day")}T${hour}:${get("minute")}`;
    const weekday = SCHEDULE_WEEKDAY_INDEX[get("weekday")];
    return weekday === undefined ? null : { minute, weekday };
  } catch {
    return null;
  }
}

/**
 * Evaluated in the schedule's own `timezone`, never the visitor's: the owner
 * picked a window meaningful to their business hours. An unresolvable zone
 * passes rather than fails, the direction every other rule here takes when it
 * cannot read its own signal (open, not closed) — see `targetsThisPage`'s
 * unreadable-`location` case for the same reasoning.
 */
function targetsScheduleRule(rules: PopupTargetingRules): boolean {
  const schedule = rules.schedule;
  if (!schedule) return true;
  const now = nowInTimeZone(schedule.timezone);
  if (!now) return true;
  if (schedule.start && now.minute < schedule.start) return false;
  if (schedule.end && now.minute > schedule.end) return false;
  if (
    schedule.weekdays &&
    schedule.weekdays.length > 0 &&
    !schedule.weekdays.includes(now.weekday)
  ) {
    return false;
  }
  return true;
}

/** All five targeting rules must pass; an absent rule passes unasked. */
function targetsTargeting(
  rules: PopupTargetingRules | undefined,
  landing: LandingContext,
  alreadySeen: boolean,
  visitorCountry: string | undefined,
): boolean {
  if (!rules) return true;
  return (
    targetsUtmRule(rules, landing) &&
    targetsReferrerRule(rules, landing) &&
    targetsVisitorRule(rules, alreadySeen) &&
    targetsScheduleRule(rules) &&
    targetsCountryRule(rules, visitorCountry)
  );
}

/** Coerce the mobile breakpoint: finite and >= 0, else the shared default. */
function toMobileBreakpoint(value: number | undefined): number {
  if (typeof value !== "number" || !Number.isFinite(value) || value < 0) {
    return DEFAULT_MOBILE_BREAKPOINT_PX;
  }
  return value;
}

/**
 * `(pointer: coarse)` as a tri-state: `null` when the browser cannot answer.
 * Wrapped because a host can be missing `matchMedia` entirely, and an engine
 * that has it can still throw on a query it cannot parse.
 */
function matchesCoarsePointer(): boolean | null {
  if (typeof window.matchMedia !== "function") return null;
  try {
    const result = window.matchMedia("(pointer: coarse)");
    return typeof result?.matches === "boolean" ? result.matches : null;
  } catch {
    return null;
  }
}

/**
 * Which device class the visitor is on.
 *
 * The primary pointer is the question, not the screen size: `(pointer: coarse)`
 * reports the pointer being used rather than the ones the hardware has, so a
 * laptop with a touchscreen stays on the computer side (its trackpad is what
 * the visitor points with) and a browser emulating a phone reports coarse.
 * Width alone would call a narrow desktop window a phone.
 *
 * Two fallbacks for a host that cannot answer the media query (an old browser,
 * a non-DOM embed): touch points, then viewport width against the same
 * breakpoint the exit signals use. Both are weaker, which is why they sit
 * behind it, and both end at "desktop" when they have nothing to go on.
 */
function currentDeviceClass(mobileBreakpointPx: number): "desktop" | "mobile" {
  if (typeof window === "undefined") return "desktop";
  const coarse = matchesCoarsePointer();
  if (coarse !== null) return coarse ? "mobile" : "desktop";
  const touchPoints =
    typeof navigator !== "undefined" ? navigator.maxTouchPoints : undefined;
  if (typeof touchPoints === "number") {
    return touchPoints > 0 ? "mobile" : "desktop";
  }
  const width = window.innerWidth;
  return Number.isFinite(width) && width > 0 && width <= mobileBreakpointPx
    ? "mobile"
    : "desktop";
}

/**
 * Standalone version of the `isTouchDevice` closure inside `createPopupTrigger`,
 * for a caller with no `PopupTriggerOptions` of its own — `popupModal.ts`'s
 * touch-only layout default (task 3.2). Always reads the engine's default
 * breakpoint rather than an owner's: `mobileBreakpoint` is engine-only (task
 * 2.3's dashboard-reachability guard has no control for it), so no form has
 * ever set it away from the default this reads, and the two never disagree.
 */
export function currentDeviceIsTouch(): boolean {
  return currentDeviceClass(DEFAULT_MOBILE_BREAKPOINT_PX) === "mobile";
}

/** Clamp a scroll percent to (0, 100]; return null when it is not usable. */
function toScrollPercent(value: number): number | null {
  if (!Number.isFinite(value) || value <= 0) return null;
  return Math.min(100, value);
}

/**
 * Parse a single trigger input into a normalized descriptor, or `null` when it
 * is unknown or malformed (so callers can drop it silently).
 */
function parseDescriptor(
  input: PopupTriggerType | string | PopupTriggerDescriptor,
): PopupTriggerDescriptor | null {
  if (input && typeof input === "object") {
    return parseObjectDescriptor(input);
  }
  if (typeof input !== "string") return null;

  const raw = input.trim();
  // Composite AND string form: "intent:warm&exit" -> {type:"all", of:[...]}.
  // Split on `&`, parse each part, drop the whole thing if nothing valid remains.
  if (raw.includes("&")) {
    const of = raw
      .split("&")
      .map((part) => part.trim())
      .filter((part) => part !== "")
      .map((part) => parseDescriptor(part))
      .filter((d): d is PopupTriggerDescriptor => d !== null);
    return of.length ? { type: "all", of } : null;
  }
  // A bare CSS selector ("#my-button", ".cta", "[data-open-popup]") is a
  // click trigger without saying so: the WordPress shortcode and the
  // `data-kiroku-trigger` attribute both document `trigger="#my-button"`
  // directly, with no `click:` prefix. Checked before the colon split so a
  // selector's own colons (`a:hover`, `[data-x="a:b"]`) stay part of it
  // rather than being read as a type prefix.
  if (/^[.#[]/.test(raw)) {
    return { type: "click", selector: raw };
  }

  const sep = raw.indexOf(":");
  const type = (sep === -1 ? raw : raw.slice(0, sep)).trim();
  // Keep the argument verbatim after the first colon: selectors may contain
  // colons (`a:hover`) and commas, so only the type prefix is split off.
  const arg = sep === -1 ? "" : raw.slice(sep + 1).trim();

  switch (type) {
    case "exit":
      return { type: "exit" };
    case "delay": {
      const ms = arg === "" ? undefined : Number(arg);
      return { type: "delay", ms: Number.isFinite(ms) ? ms : undefined };
    }
    case "inactivity": {
      const ms = arg === "" ? undefined : Number(arg);
      return { type: "inactivity", ms: Number.isFinite(ms) ? ms : undefined };
    }
    case "scroll": {
      if (arg === "") return null; // bare `scroll` has no threshold: drop it
      const percent = toScrollPercent(Number(arg));
      return percent === null ? null : { type: "scroll", percent };
    }
    case "click":
      return arg === "" ? null : { type: "click", selector: arg };
    case "visible":
      return arg === "" ? null : { type: "visible", selector: arg };
    case "intent": {
      // `intent` (bare) or `intent:warm` fire on warm+, `intent:hot` on hot only.
      // An unknown argument falls back to the warm default rather than dropping.
      const tier = arg === "hot" ? "hot" : arg === "warm" ? "warm" : undefined;
      return { type: "intent", tier };
    }
    default:
      return null;
  }
}

/** Validate an object descriptor (used when callers pass structured input). */
function parseObjectDescriptor(
  input: PopupTriggerDescriptor,
): PopupTriggerDescriptor | null {
  switch (input.type) {
    case "exit":
      return { type: "exit" };
    case "delay": {
      const ms = input.ms;
      return {
        type: "delay",
        ms: typeof ms === "number" && Number.isFinite(ms) ? ms : undefined,
      };
    }
    case "inactivity": {
      const ms = input.ms;
      return {
        type: "inactivity",
        ms: typeof ms === "number" && Number.isFinite(ms) ? ms : undefined,
      };
    }
    case "scroll": {
      const percent = toScrollPercent(Number(input.percent));
      return percent === null ? null : { type: "scroll", percent };
    }
    case "click": {
      const selector =
        typeof input.selector === "string" ? input.selector.trim() : "";
      return selector === "" ? null : { type: "click", selector };
    }
    case "visible": {
      const selector =
        typeof input.selector === "string" ? input.selector.trim() : "";
      return selector === "" ? null : { type: "visible", selector };
    }
    case "intent": {
      const tier =
        input.tier === "hot"
          ? "hot"
          : input.tier === "warm"
            ? "warm"
            : undefined;
      return { type: "intent", tier };
    }
    case "all": {
      const of = Array.isArray(input.of)
        ? input.of
            .map((sub) => parseDescriptor(sub))
            .filter((d): d is PopupTriggerDescriptor => d !== null)
        : [];
      return of.length ? { type: "all", of } : null;
    }
    default:
      return null;
  }
}

/**
 * Scroll depth used when a stored form selects the `scroll` trigger without a
 * threshold. Half the page is the mid-point of the usable range and matches the
 * dashboard's initial value; the alternative (drop the trigger) leaves the form
 * owner with a popup that silently never opens.
 */
export const DEFAULT_SCROLL_PERCENT = 50;

/**
 * One trigger plus its parameter, as stored on `settings.popup` (and on each
 * half of an AND pair). Structural on purpose: `src/lib/types.ts` owns the
 * dashboard-facing shape and this engine stays free of app imports.
 */
export interface PopupSettingsTriggerSlot {
  trigger?: string;
  delayMs?: number;
  scrollPercent?: number;
  intentLevel?: string;
  /** CSS selector for `click` (element clicked) and `visible` (element seen). */
  selector?: string;
}

/** The stored popup settings the engine can build a descriptor from. */
export interface PopupSettingsTriggerInput extends PopupSettingsTriggerSlot {
  and?: PopupSettingsTriggerSlot[] | null;
}

/** Build a descriptor from one settings slot, or `null` when unusable. */
function slotToDescriptor(
  slot: PopupSettingsTriggerSlot | null | undefined,
): PopupTriggerDescriptor | null {
  if (!slot || typeof slot !== "object") return null;
  switch (slot.trigger) {
    case "exit":
      return { type: "exit" };
    case "delay":
      return parseObjectDescriptor({ type: "delay", ms: slot.delayMs });
    case "inactivity":
      return parseObjectDescriptor({ type: "inactivity", ms: slot.delayMs });
    case "scroll":
      return {
        type: "scroll",
        percent:
          toScrollPercent(Number(slot.scrollPercent)) ?? DEFAULT_SCROLL_PERCENT,
      };
    case "intent":
      return parseObjectDescriptor({
        type: "intent",
        tier: slot.intentLevel as PopupIntentTier | undefined,
      });
    // `click`/`visible` read the element from `selector`. A blank one has no
    // element to watch, so parseObjectDescriptor drops it rather than arming a
    // trigger that can never fire.
    case "click":
      return parseObjectDescriptor({
        type: "click",
        selector: slot.selector as string,
      });
    case "visible":
      return parseObjectDescriptor({
        type: "visible",
        selector: slot.selector as string,
      });
    // An unknown name is a typo, not a trigger: drop it.
    default:
      return null;
  }
}

/**
 * Convert stored popup settings into the descriptor the engine arms. Returns
 * `null` when the settings describe nothing armable, so the caller can pass an
 * empty trigger list and leave the popup closed.
 *
 * `trigger: "all"` reads its two halves from `and`; every other trigger reads
 * its own parameter off the top level. A trigger saved before its parameter
 * field existed falls back to that parameter's default rather than dropping.
 */
export function popupSettingsToTrigger(
  settings: PopupSettingsTriggerInput | null | undefined,
): PopupTriggerDescriptor | null {
  if (!settings || typeof settings !== "object") return null;
  if (settings.trigger === "all") {
    const of = (Array.isArray(settings.and) ? settings.and : [])
      .map((slot) => slotToDescriptor(slot))
      .filter((d): d is PopupTriggerDescriptor => d !== null);
    return of.length ? { type: "all", of } : null;
  }
  return slotToDescriptor(settings);
}

/**
 * The stored mobile-trigger choice (task 3.1 of
 * docs/popup-triggers-and-funnel_plan.md), structural like
 * `PopupSettingsTriggerSlot` above for the same reason: `src/lib/types.ts`
 * owns the dashboard-facing shape (`FormPopupMobileTrigger`) and this engine
 * stays free of `@/lib` imports.
 */
export interface PopupSettingsMobileTriggerInput {
  trigger?: string;
  scrollPercent?: number;
  delayMs?: number;
}

/**
 * Convert the stored mobile-trigger choice into the descriptor
 * `createPopupTrigger`'s `mobileTrigger` option reads, or `null` for `"same"`
 * (or an absent/unknown choice), which the caller passes through as
 * `undefined`: no override, so a touch visitor arms `trigger` unchanged, same
 * as a form saved before this field existed.
 *
 * `"scroll"` and `"delay"` each read their own parameter off the top level,
 * the same field names the top-level trigger of the same name reads;
 * `"all"` reads both and combines them the same way a stored `trigger: "all"`
 * combines its `and` pair.
 */
export function mobilePopupSettingsToTrigger(
  settings: PopupSettingsMobileTriggerInput | null | undefined,
): PopupTriggerDescriptor | null {
  if (!settings || typeof settings !== "object") return null;
  switch (settings.trigger) {
    case "scroll":
      return slotToDescriptor({
        trigger: "scroll",
        scrollPercent: settings.scrollPercent,
      });
    case "delay":
      return slotToDescriptor({ trigger: "delay", delayMs: settings.delayMs });
    case "all": {
      const of = [
        slotToDescriptor({
          trigger: "scroll",
          scrollPercent: settings.scrollPercent,
        }),
        slotToDescriptor({ trigger: "delay", delayMs: settings.delayMs }),
      ].filter((d): d is PopupTriggerDescriptor => d !== null);
      return of.length ? { type: "all", of } : null;
    }
    // "same", absent, or an unknown value: no override.
    default:
      return null;
  }
}

/**
 * Parse any accepted trigger input into the descriptors the engine would arm,
 * without arming anything. Exposed so callers can compare what they are about
 * to store against the `data-kiroku-trigger` string form a developer would
 * hand-write, which is the same parse path `arm` runs.
 */
export function parseTriggerInput(
  input: PopupTriggerInput,
): PopupTriggerDescriptor[] {
  return normalizeTriggers(input);
}

/**
 * The trigger names a comma-separated list can start a new entry with. Built
 * from the same list the parser switches on, so a new trigger type is split
 * correctly the moment it is added.
 */
const TRIGGER_TYPE_NAMES = [
  "exit",
  "delay",
  "scroll",
  "click",
  "visible",
  "inactivity",
  "intent",
] as const;
const STARTS_A_TRIGGER = new RegExp(
  `^\\s*(?:${TRIGGER_TYPE_NAMES.join("|")})\\s*(?::|&|$)`,
);

/**
 * Split a string carrying several triggers ("scroll:50, click:#cta") into its
 * entries. A CSS selector list is comma-separated too, so a comma only starts a
 * new entry when what follows it names a trigger; anything else ("click:.a, .b")
 * belongs to the argument of the entry before it. The embed hands
 * `data-kiroku-trigger` through verbatim, and the developer guides document the
 * comma form, so without this the documented attribute armed nothing.
 */
function splitTriggerList(raw: string): string[] {
  const entries: string[] = [];
  for (const part of raw.split(",")) {
    if (entries.length && !STARTS_A_TRIGGER.test(part)) {
      entries[entries.length - 1] += `,${part}`;
    } else {
      entries.push(part);
    }
  }
  return entries.map((entry) => entry.trim()).filter((entry) => entry !== "");
}

/** Normalize the `trigger` option to a list of valid descriptors. */
function normalizeTriggers(input: PopupTriggerInput): PopupTriggerDescriptor[] {
  const list = Array.isArray(input) ? input : [input];
  const out: PopupTriggerDescriptor[] = [];
  for (const item of list) {
    const entries = typeof item === "string" ? splitTriggerList(item) : [item];
    for (const entry of entries) {
      const descriptor = parseDescriptor(entry);
      if (descriptor) out.push(descriptor);
    }
  }
  return out;
}

/**
 * True for a trigger the visitor asks for on purpose, which is `click` and only
 * at the top level. Task 4.1 makes these opens repeatable and uncapped; the
 * other seven are uninvited and keep the one-shot latch.
 *
 * A `click` inside an `all` pair is deliberately not one of these. There the
 * pair is the trigger and the press is one of its conditions rather than the
 * whole request, so the composite's own `hit` goes through the shared one-shot
 * `fire` and "press the button after scrolling half way" still opens once.
 */
function isUserInitiated(desc: PopupTriggerDescriptor): boolean {
  return desc.type === "click";
}

/** True when `selector` is syntactically valid (existence not required). */
function isValidSelector(selector: string): boolean {
  try {
    document.querySelector(selector);
    return true;
  } catch {
    return false; // invalid CSS: never attach a listener for it
  }
}

/**
 * A storage wrapper that never throws. Wraps the injected/localStorage backend
 * and transparently falls back to an in-memory map when a read or write fails.
 *
 * The second constructor argument picks which real backend the default
 * (uninjected) case probes: `window.localStorage` for the persisted cap state,
 * `window.sessionStorage` for the per-session show mark task 1.2 adds. Both
 * degrade to the same in-memory map on a throw, so a blocked or private-mode
 * backend fails safe (holds for the life of the instance) either way.
 */
class SafeStorage {
  private backend: StorageLike | null;
  private readonly memory = new Map<string, string>();

  constructor(
    injected?: StorageLike,
    detect: () => StorageLike | null = SafeStorage.detectLocalStorage,
  ) {
    this.backend = injected ?? detect();
  }

  private static detectLocalStorage(): StorageLike | null {
    return SafeStorage.probe(() => window.localStorage);
  }

  static detectSessionStorage(): StorageLike | null {
    return SafeStorage.probe(() => window.sessionStorage);
  }

  private static probe(getBackend: () => Storage): StorageLike | null {
    try {
      const backend = getBackend();
      const probe = "__kiroku_probe__";
      backend.setItem(probe, "1");
      backend.removeItem(probe);
      return backend;
    } catch {
      return null;
    }
  }

  read(key: string): string | null {
    if (this.backend) {
      try {
        return this.backend.getItem(key);
      } catch {
        this.backend = null; // stop trusting a throwing backend
      }
    }
    return this.memory.has(key) ? (this.memory.get(key) as string) : null;
  }

  write(key: string, value: string): void {
    this.memory.set(key, value);
    if (this.backend) {
      try {
        this.backend.setItem(key, value);
      } catch {
        this.backend = null; // degrade to in-memory only, silently
      }
    }
  }
}

export function createPopupTrigger(options: PopupTriggerOptions): PopupTrigger {
  // Raw stored delay/idle length (owner-set, or absent). Left uncoerced here:
  // the right default and, on a touch device, the floor (task 2.2) depend on
  // the device, which `armDelay`/`armInactivity` read live through
  // `resolveTimerMs` rather than at creation time.
  const delayMs = options.delayMs;
  const frequencyDays = toFrequencyDays(options.frequencyDays);
  const maxShows = toMaxShows(options.maxShows);
  const declineSuppressDays = toDeclineSuppressDays(
    options.declineSuppressDays,
  );
  const minTimeOnPage = toMinTimeOnPage(options.minTimeOnPageMs);
  const devices = toDeviceTarget(options.devices);
  const pageTarget = compilePageTarget(options.pages);
  const storage = new SafeStorage(options.storage);
  const stateKey = options.formId ? KEY_PREFIX + options.formId : null;
  // frequencyDays === 0 means "once per session" (task 1.2), which needs its
  // own mark distinct from `stateKey`'s persisted shownAt: sessionStorage
  // clears on tab close where localStorage would not, and a mark that lived in
  // the same record would survive into the next session and cap it too.
  //
  // Created on first use: only a `frequencyDays === 0` popup reads or writes
  // it, and detecting it writes and removes a probe key, which every other
  // popup has no reason to do. Probing eagerly also queued two jsdom storage
  // events per trigger, which is how
  // tests/popup-signal-parity.test.ts ("a click popup stays bound") caught it.
  let sessionMarkStorageLazy: SafeStorage | null = null;
  const sessionMarkStorage = (): SafeStorage =>
    (sessionMarkStorageLazy ??= new SafeStorage(
      undefined,
      SafeStorage.detectSessionStorage,
    ));
  const sessionKey = options.formId
    ? SESSION_KEY_PREFIX + options.formId
    : null;

  let fired = false;
  let armed = false;
  // Visible, recently-active time since the first inferred trigger asked for
  // it (task 2.1): gates `minTimeOnPageMs` for `scroll`/`visible`/`intent`
  // below and times the `delay` trigger, in place of plain wall-clock, so a
  // tab hidden or a visitor who has touched nothing in three seconds stops
  // both clocks the way it already stops `exit`'s own guard in
  // exitSignalsNative.ts. Lazy like `sessionMarkStorage` below: a `click`-only
  // popup never reads it and must not pay for one.
  let activeClockLazy: ActiveTimeClock | null = null;
  const activeClock = (): ActiveTimeClock =>
    (activeClockLazy ??= createActiveTimeClock());
  // Teardown thunks for every armed trigger (timer/listener/observer).
  const cleanups: Array<() => void> = [];
  // What the host page's consent signal said. Resolved on the first ask and
  // kept, so the several places that will ask (the beacon on each of three
  // events, and the dashboard reading the reason) see one answer from one
  // moment rather than re-reading a value that may have changed underneath
  // them. A signal that changes mid-session is a revoke, which is its own path
  // rather than a re-read hidden inside a getter.
  let consentResolution: TrackingConsentResolution | null = null;
  // The interval watching for a withdrawal, and whether one has happened.
  // Revocation is terminal: the engine does not come back if the visitor
  // answers yes again, because acting late on a yes costs an uncounted popup
  // and acting late on a no costs a transmission we were told not to make.
  const consentPollMs = toConsentPollMs(options.consentPollMs);
  let consentWatcher: ReturnType<typeof setInterval> | null = null;
  let trackingRevoked = false;

  /**
   * Consult the posture. Reads the signal at most once per controller; every
   * later ask returns the same answer, until a revoke replaces it.
   */
  /**
   * Whether this popup may write its own re-show record (`kiroku:popup:*`) to
   * the visitor's browser. Decision 0.7 of docs/consent-management_plan.md: only
   * a resolved denial stops the write. A page with no consent source, or a
   * banner that has not answered yet, resolves to allowed and keeps writing,
   * so a customer with no cookie banner still gets a working frequency cap.
   */
  function mayRecord(): boolean {
    return consultConsent().allowed !== false;
  }

  function consultConsent(): TrackingConsentResolution {
    if (!consentResolution) {
      consentResolution = resolveTrackingConsent(
        options.consent ?? {},
        options.consentEnvironment ?? {},
      );
      // From the moment a posture is first acted on, whether that was `arm` or
      // a beacon asking on a popup the host opened itself.
      startConsentWatch();
    }
    return consentResolution;
  }

  /**
   * Watch the configured signal for a change of answer. See
   * DEFAULT_CONSENT_POLL_MS for why this polls rather than subscribing.
   *
   * Started only when the answer was yes. An answer that was already no has
   * nothing to withdraw, and starting here anyway would tear down the popup of
   * a visitor whose banner said no before the page loaded, which decision 0.2
   * of docs/consent-management_plan.md and task 4.2 both keep working.
   */
  function startConsentWatch(): void {
    if (consentWatcher || trackingRevoked) return;
    if (consentPollMs <= 0) return;
    if (!consentResolution?.allowed) return;
    if (!hasConsentSource(options.consent)) return;
    consentWatcher = setInterval(() => {
      const next = resolveTrackingConsent(
        options.consent ?? {},
        options.consentEnvironment ?? {},
      );
      // Only a no acts. A signal that has become unreadable is not a
      // withdrawal, which is the rule `./trackingConsent` already applies at
      // arming time: a value we cannot read is not an answer either way.
      if (next.allowed) return;
      revokeTracking(next.source);
    }, consentPollMs);
  }

  function stopConsentWatch(): void {
    if (!consentWatcher) return;
    clearInterval(consentWatcher);
    consentWatcher = null;
  }

  /** See `revokeTracking` on the PopupTrigger interface for what this covers. */
  function revokeTracking(
    source: TrackingConsentSource | null = null,
  ): TrackingConsentResolution {
    if (trackingRevoked && consentResolution) return consentResolution;
    const resolved: TrackingConsentResolution = {
      state: "denied",
      // Which source carried the withdrawal, so the dashboard can say where the
      // answer came from; falls back to whichever source answered at arming.
      source: source ?? consentResolution?.source ?? null,
      allowed: false,
      reason: TRACKING_REVOKED_REASON,
    };
    trackingRevoked = true;
    consentResolution = resolved;
    stopConsentWatch();
    cleanup();
    try {
      options.onTrackingRevoked?.(resolved);
    } catch {
      /* a host callback throwing must not break the page it runs on */
    }
    return resolved;
  }

  function readState(): PopupState {
    if (!stateKey) return {};
    const raw = storage.read(stateKey);
    if (!raw) return {};
    try {
      const parsed = JSON.parse(raw) as PopupState;
      return parsed && typeof parsed === "object" ? parsed : {};
    } catch {
      return {};
    }
  }

  function writeState(patch: Partial<PopupState>): void {
    if (!stateKey) return;
    const next: PopupState = { ...readState(), ...patch };
    storage.write(stateKey, JSON.stringify(next));
  }

  /**
   * True when re-show is currently capped. Three independent limits, strictest
   * wins. An explicit decline suppresses for its own (longer) window,
   * independent of `frequencyDays` and consulted even when `frequencyDays` is 0
   * (per-session). A submission suppresses for `CONVERTED_SUPPRESS_DAYS` on
   * the same terms. The show-count cap is a total across the visitor's whole
   * history, so it is consulted on the same terms and outlives any window. A
   * passive show otherwise suppresses inside the `frequencyDays` window, or for
   * the rest of the tab session when `frequencyDays` is 0 (task 1.2: the
   * session mark in `sessionKey`, not the persisted `shownAt`).
   *
   * Without a `formId` there is no state key (or session key) and nothing is
   * counted, so the cap reads 0 shows so far and lets the popup open. An
   * uncounted show beats a popup permanently capped because it has nowhere to
   * write.
   */
  function isSuppressed(): boolean {
    const state = readState();
    if (
      state.declinedAt &&
      Date.now() - state.declinedAt < declineSuppressDays * DAY_MS
    ) {
      return true;
    }
    if (
      state.convertedAt &&
      Date.now() - state.convertedAt < CONVERTED_SUPPRESS_DAYS * DAY_MS
    ) {
      return true;
    }
    if (maxShows !== null && toShownCount(state.shownCount) >= maxShows) {
      return true;
    }
    if (frequencyDays === 0) {
      // Per session: capped once this tab session has already shown it, read
      // from sessionStorage rather than `state.shownAt` so the mark clears
      // with the tab (a reload keeps it, a new session does not).
      return (
        sessionKey !== null && sessionMarkStorage().read(sessionKey) !== null
      );
    }
    const last = state.shownAt ?? 0;
    if (!last) return false;
    return Date.now() - last < frequencyDays * DAY_MS;
  }

  /** Fraction of the page scrolled, as a percent in [0, 100]. */
  function currentScrollPercent(): number {
    const doc = document.documentElement;
    const scrollTop =
      window.scrollY || window.pageYOffset || doc.scrollTop || 0;
    const max = doc.scrollHeight - doc.clientHeight;
    if (max <= 0) return 0; // nothing to scroll: threshold can never be met
    return Math.min(100, Math.max(0, (scrollTop / max) * 100));
  }

  /**
   * Cancels an open deferred by `openWhenVisible`. Kept apart from `cleanups`
   * because `fire` empties those before it defers; `disarm()` and a consent
   * revoke both run `cleanup`, so either still stops a deferred open.
   */
  let cancelDeferredOpen: (() => void) | null = null;

  /**
   * Open now if the page is visible and focused, otherwise when both are true.
   *
   * On a phone the commonest "exit" is the tab going hidden (and `blur` can
   * fire as it does). Opening then drew a modal nobody could see, logged an
   * impression, wrote the frequency cap and understated conversion per show:
   * a visitor who switched away was capped for a month without ever seeing
   * the popup. Ben's decision, 2026-09-24
   * (docs/popup-experiment-factors_research.md §1): count a show only when it
   * is visible, and open when they return. The caller's `onFire` is what
   * opens the modal and records the show, so deferring it defers both.
   *
   * Focus joins the same gate (task 1.2): a window that merely lost focus
   * (Cmd-Tab to another app, a click into another window) stays `visible`, so
   * the visibility check alone opened a popup in a window nobody was looking
   * at, counted the show and started the frequency cap on it.
   * `document.hasFocus()` is read live rather than tracked, the same way
   * `document.visibilityState` already is a few lines up: tracking would mean
   * a `blur`/`focus` listener bound for the life of every armed trigger, not
   * only the ones that end up deferring, which is the engine's whole listener
   * surface and the thing `tests/popup-signal-parity.test.ts` pins per
   * trigger type. A `blur` caused by focus moving into a same-page iframe
   * does not strand the popup either: nothing here latches "blurred" the way
   * `fired` is latched, so a stray blur with no real departure behind it is
   * superseded the moment the `focus` that follows is read.
   */
  function openWhenVisible(onFire: () => void): void {
    const hasFocus = () =>
      typeof document.hasFocus !== "function" || document.hasFocus();
    const ready = () =>
      typeof document === "undefined" ||
      (document.visibilityState !== "hidden" && hasFocus());
    if (ready()) {
      onFire();
      return;
    }
    const onReady = () => {
      if (!ready()) return;
      cancelDeferredOpen?.();
      // Another tab may have shown or converted it while this one was hidden.
      if (isSuppressed()) return;
      onFire();
    };
    document.addEventListener("visibilitychange", onReady);
    window.addEventListener("focus", onReady);
    cancelDeferredOpen = () => {
      document.removeEventListener("visibilitychange", onReady);
      window.removeEventListener("focus", onReady);
      cancelDeferredOpen = null;
    };
  }

  function cleanup(): void {
    cancelDeferredOpen?.();
    while (cleanups.length) {
      const teardown = cleanups.pop();
      try {
        teardown?.();
      } catch {
        /* a teardown throwing must not block the others */
      }
    }
    activeClockLazy?.stop();
    armed = false;
  }

  /**
   * Run `onFire` if allowed. Returns `true` only when it actually fired, so a
   * caller (the click trigger) can suppress an anchor's default nav only when
   * the popup really opened. Returns `false` when already latched, when the
   * trigger has been torn down, or when the frequency/decline cap suppresses at
   * fire time.
   *
   * `userInitiated` is the `click` trigger and nothing else (task 4.1). It does
   * not latch `fired`, does not `cleanup()` and does not consult
   * `isSuppressed()`, so the same button opens the popup on every press. A cap
   * exists to limit interruption and somebody pressing "talk to us" is not
   * being interrupted; without this the button opens once and is then inert,
   * which reads as broken rather than as capped.
   *
   * `armed` is consulted on both paths, because that is teardown and not a cap.
   * `disarm()` and a consent revoke both clear it, and a visitor who has just
   * asked us to stop watching them does not get a popup for pressing something.
   *
   * `reason` (task 4.1 of docs/popup-triggers-and-funnel_plan.md) is carried
   * through to `onFire` unexamined: this function decides whether to fire, not
   * why, so it never reads it.
   */
  function fire(
    onFire: (userInitiated: boolean, reason: string) => void,
    userInitiated: boolean,
    reason: string,
  ): boolean {
    if (fired && !userInitiated) return false;
    // Torn down: `cleanup` clears this, so a signal that arrives afterwards
    // opens nothing. Every listener and timer is detached by then, and one
    // source is still outside our reach. `IntersectionObserver.disconnect()`
    // empties [[ObservationTargets]] and says nothing about [[QueuedEntries]],
    // and "notify intersection observers" invokes the callback for any observer
    // whose queue is non-empty, so an entry queued before teardown still
    // arrives afterwards. Without this the `visible` trigger would open a popup
    // on a page a single-page host had already left.
    if (!armed) return false;
    if (!userInitiated) {
      // Re-check the cap at fire time in case state changed after arming.
      if (isSuppressed()) {
        cleanup();
        return false;
      }
      fired = true;
      cleanup();
      openWhenVisible(() => onFire(false, reason));
      return true;
    }
    onFire(true, reason);
    return true;
  }

  /**
   * `ms` of visible, recently-active time (task 2.1), not wall-clock: a tab
   * hidden or three seconds untouched stops the countdown the same way it
   * stops the arming guard below, so a `delay` popup does not open the moment
   * someone switches back after being away far longer than `ms`. The length
   * itself goes through `resolveTimerMs` (task 2.2), so a touch device gets
   * the phone default and floor rather than whatever a computer was tuned for.
   */
  function armDelay(
    desc: { ms?: number },
    hit: (reason: string) => boolean,
  ): void {
    const ms = resolveTimerMs(desc.ms ?? delayMs, isTouchDevice());
    cleanups.push(activeClock().whenElapsed(ms, () => hit("delay")));
  }

  /**
   * Exit intent, delegated to `exitSignalsNative.ts`: a top-ward `mouseout`
   * with a grace delay, plus tab-hidden, window blur, a fast scroll to the top
   * and the Cmd+L address bar shortcut. Only the first needs a cursor, which is
   * why the mouse-capability branch this used to open with is gone: a touch
   * visitor got `armDelay` here, a timer standing in for a signal nobody was
   * listening for.
   *
   * Which signals bind depends on the device (task 2.1,
   * docs/popup-exit-signals-fix_plan.md): a computer visitor switching
   * windows, hiding the tab or pressing Cmd+L is routinely still reading, not
   * leaving, so only the mouse leaving through the top and a fast scroll to
   * the top count there. A touch visitor has no mouse to be doing something
   * else with, so all five stay armed, unchanged from before this task.
   *
   * The module holds the arming guard for this trigger, so the length is passed
   * in rather than left to its own default: one value, configured in one place.
   *
   * The top-edge band, the grace delay and the scroll-up thresholds go through
   * verbatim: the module repairs a bad value (see `toNonNegative` there), so a
   * host calling it directly gets the same treatment as a form going through
   * the engine.
   */
  function armExit(hit: (reason: string) => boolean): void {
    cleanups.push(
      armNativeExitSignals(hit, {
        minTimeOnPageMs: minTimeOnPage,
        exitSensitivityPx: options.exitSensitivityPx,
        mouseLeaveDelayMs: options.mouseLeaveDelayMs,
        scrollUpThreshold: options.scrollUpThreshold,
        mobileBreakpoint: options.mobileBreakpoint,
        scrollUpWindowMs: options.scrollUpWindowMs,
        signals: isTouchDevice() ? TOUCH_EXIT_SIGNALS : COMPUTER_EXIT_SIGNALS,
      }),
    );
  }

  function armScroll(
    desc: { percent: number },
    hit: (reason: string) => boolean,
  ): void {
    const handler = () => {
      if (currentScrollPercent() >= desc.percent) {
        hit("scroll");
      }
    };
    window.addEventListener("scroll", handler, { passive: true });
    cleanups.push(() => window.removeEventListener("scroll", handler));
    // Cover an already-scrolled page (restored position, deep link).
    handler();
  }

  function armClick(
    desc: { selector: string },
    hit: (reason: string) => boolean,
  ): void {
    if (!isValidSelector(desc.selector)) return; // bad selector: no-op
    const handler = (event: MouseEvent) => {
      const target = event.target;
      if (!(target instanceof Element)) return;
      const matched = target.closest(desc.selector);
      if (!matched) return;
      // Suppress the anchor's default nav only when the popup actually opens,
      // so a JS-off click, an already-fired latch, or a suppressed cap all keep
      // the `href` fallback nav alive (progressive enhancement).
      if (hit("click") && matched.closest("a[href]")) {
        event.preventDefault();
      }
    };
    document.addEventListener("click", handler);
    cleanups.push(() => document.removeEventListener("click", handler));
  }

  function armVisible(
    desc: { selector: string },
    hit: (reason: string) => boolean,
  ): void {
    if (typeof IntersectionObserver === "undefined") return; // degrade quietly
    if (!isValidSelector(desc.selector)) return;
    const el = document.querySelector(desc.selector);
    if (!el) return; // nothing to observe: no-op
    const observer = new IntersectionObserver((entries) => {
      for (const entry of entries) {
        if (entry.isIntersecting) {
          hit("visible");
          return;
        }
      }
    });
    observer.observe(el);
    cleanups.push(() => observer.disconnect());
  }

  function armInactivity(
    desc: { ms?: number },
    hit: (reason: string) => boolean,
  ): void {
    // Device-aware default and floor (task 2.2), same as `armDelay`: a touch
    // device never goes idle-fires faster than `MOBILE_DELAY_FLOOR_MS`.
    const ms = resolveTimerMs(
      desc.ms ?? delayMs,
      isTouchDevice(),
      DEFAULT_INACTIVITY_MS,
    );
    let timerId = setTimeout(() => hit("inactivity"), ms);
    // Any interaction restarts the idle countdown; the window fires only once
    // `ms` elapse with no reset. After `fire`, `cleanup` removes these
    // listeners, so no reset can re-arm past the one-shot latch.
    const reset = (): void => {
      clearTimeout(timerId);
      timerId = setTimeout(() => hit("inactivity"), ms);
    };
    for (const eventName of INACTIVITY_EVENTS) {
      document.addEventListener(eventName, reset, {
        passive: true,
        capture: true,
      });
    }
    cleanups.push(() => {
      clearTimeout(timerId);
      for (const eventName of INACTIVITY_EVENTS) {
        // The capture flag is part of what identifies a listener: removing
        // without it leaves every one of these bound for the life of the page.
        document.removeEventListener(eventName, reset, { capture: true });
      }
    });
  }

  /**
   * Attaches as soon as `intent` is armed, not once the guard elapses (task
   * 2.3): the tracker sends its warm/hot event once per page load, so a
   * listener attached late misses the one event it exists to catch, and the
   * (`intent`, `exit`) pair could never complete for a visitor who qualified
   * early. The guard still holds the *firing*: a qualifying tier seen before
   * the guard elapses is remembered, not acted on, until `whenElapsed` says
   * the visit is old enough to mean anything; a qualifying tier seen after
   * fires at once, as it always did.
   */
  function armIntent(
    desc: { tier?: PopupIntentTier },
    hit: (reason: string) => boolean,
  ): void {
    const min = TIER_RANK[desc.tier ?? "warm"];
    let qualified = false;
    let guardElapsed = false;
    const fireIfReady = (): void => {
      if (qualified && guardElapsed) hit("intent");
    };
    const handler = (event: Event): void => {
      const detail = (event as CustomEvent).detail;
      const tier = detail && typeof detail.tier === "string" ? detail.tier : "";
      if ((TIER_RANK[tier] ?? -1) >= min) {
        qualified = true;
        fireIfReady();
      }
    };
    document.addEventListener(VM_INTENT_EVENT, handler);
    cleanups.push(() => document.removeEventListener(VM_INTENT_EVENT, handler));
    cleanups.push(
      activeClock().whenElapsed(minTimeOnPage, () => {
        guardElapsed = true;
        fireIfReady();
      }),
    );
  }

  /**
   * Composite AND. Arms every sub-trigger with its own one-shot latch; only when
   * ALL have latched does it call `hit` (which fires the popup, once). Sub-latches
   * are order-independent and persist until `cleanup`, so an "intent + exit" pair
   * opens on the exit that follows the session having already scored warm.
   *
   * Each sub's own reason (task 2.2) is kept, not discarded: the pair's `hit` is
   * called with `all:<reason>+<reason>`, in descriptor order, because a popup
   * that opens uninvited has to be diagnosable, and "all" alone cannot say
   * whether it was scroll+mouse_top or intent+blur.
   */
  function armAll(
    subs: PopupTriggerDescriptor[],
    hit: (reason: string) => boolean,
  ): void {
    if (subs.length === 0) return;
    const satisfied = new Set<number>();
    const reasons: string[] = new Array(subs.length);
    subs.forEach((sub, i) => {
      armDescriptor(sub, (reason) => {
        satisfied.add(i);
        reasons[i] = reason;
        return satisfied.size === subs.length
          ? hit(`all:${reasons.join("+")}`)
          : false;
      });
    });
  }

  /**
   * True when this visitor's primary pointer is touch (task 2.2's phone
   * default and floor for `delay`/`inactivity`). Distinct from
   * `targetsThisDevice` just below: that answers whether the popup is for
   * this visitor at all, this answers how long to wait once it is armed, and
   * a popup targeted at `devices: "all"` still needs the right one for
   * whichever device actually opens it.
   */
  function isTouchDevice(): boolean {
    return (
      currentDeviceClass(toMobileBreakpoint(options.mobileBreakpoint)) ===
      "mobile"
    );
  }

  /**
   * True when this visitor's device is one the popup is meant for. "all"
   * answers without asking, so the default (and every form stored before the
   * field existed) never reads `matchMedia` at all.
   */
  function targetsThisDevice(): boolean {
    if (devices === "all") return true;
    return (
      currentDeviceClass(toMobileBreakpoint(options.mobileBreakpoint)) ===
      devices
    );
  }

  /**
   * The descriptors this visitor's device actually arms (task 3.1). A touch
   * visitor with a configured `mobileTrigger` arms that instead of `trigger`;
   * everyone else, and every touch visitor with no override, arms `trigger`
   * unchanged. Read once, in `arm`, the same moment `targetsThisDevice` and
   * `isTouchDevice` are: the device class does not change mid-session, so
   * nothing here needs to react to a resize the way a CSS media query would.
   */
  function resolveDescriptors(): PopupTriggerDescriptor[] {
    if (options.mobileTrigger !== undefined && isTouchDevice()) {
      return normalizeTriggers(options.mobileTrigger);
    }
    return normalizeTriggers(options.trigger);
  }

  /**
   * True when this page is one the popup is meant for. No rule answers without
   * asking, so the default (and every form stored before the field existed)
   * never reads `location`, the way `devices === "all"` never reaches
   * `matchMedia`.
   *
   * Exclude is checked first and wins over include, which is the order people
   * expect ("everywhere under /blog except /blog/private") and the one that
   * fails safe: the mistake it produces is a popup that does not show, never
   * one that shows on a page the owner forbade. An empty include list means
   * every page, so an exclude-only rule reads as "everywhere except".
   */
  function targetsThisPage(): boolean {
    if (pageTarget === null) return true;
    const path = currentPathname();
    // Nothing readable to match against. Opening is the same answer as no rule
    // and the one that cannot silently hide a popup the owner switched on.
    if (path === null) return true;
    if (pageTarget.exclude.some((matcher) => matcher.test(path))) return false;
    if (pageTarget.include.length === 0) return true;
    return pageTarget.include.some((matcher) => matcher.test(path));
  }

  /**
   * Arm a descriptor, holding the inferred triggers back until the guard has
   * elapsed. `scroll` and `visible` attach late rather than fire late, and
   * nothing is queued while they wait: the guard running out opens nothing by
   * itself. Attaching is what re-reads the page, so they still open for a
   * visitor who was already past the threshold before the guard lifted,
   * instead of having their one signal dropped and never opening at all.
   *
   * `intent` is the exception (task 2.3): it has no page state to re-read on
   * late attach, only a one-shot event the tracker sends once per page load,
   * so attaching late can miss the only event there is. `armIntent` attaches
   * now and holds the firing itself, against the same guard, so it still
   * reaches `armNow` here rather than through the `whenElapsed` below.
   *
   * The wait is visible, recently-active time (task 2.1), not wall-clock:
   * `whenElapsed` re-arms itself around a hidden tab or three untouched
   * seconds the same way `armDelay` does, so a form stored before this guard
   * existed is held for the active minutes it was always meant to measure.
   */
  function armDescriptor(
    desc: PopupTriggerDescriptor,
    hit: (reason: string) => boolean,
  ): void {
    if (desc.type === "intent") {
      armNow(desc, hit);
      return;
    }
    if (HELD_UNTIL_GUARD_ELAPSES.has(desc.type)) {
      cleanups.push(
        activeClock().whenElapsed(minTimeOnPage, () => armNow(desc, hit)),
      );
      return;
    }
    armNow(desc, hit);
  }

  function armNow(
    desc: PopupTriggerDescriptor,
    hit: (reason: string) => boolean,
  ): void {
    switch (desc.type) {
      case "exit":
        armExit(hit);
        return;
      case "delay":
        armDelay(desc, hit);
        return;
      case "scroll":
        armScroll(desc, hit);
        return;
      case "click":
        armClick(desc, hit);
        return;
      case "visible":
        armVisible(desc, hit);
        return;
      case "inactivity":
        armInactivity(desc, hit);
        return;
      case "intent":
        armIntent(desc, hit);
        return;
      case "all":
        armAll(desc.of, hit);
        return;
    }
  }

  return {
    arm(onFire: (userInitiated: boolean, reason: string) => void): void {
      if (armed || fired) return;
      // Torn down for consent reasons and staying down. Re-arming here would
      // attach listeners to a visitor who has just asked us to stop watching
      // them, which is the one thing a revoke has to make impossible.
      if (trackingRevoked) return;
      const descriptors = resolveDescriptors();
      if (descriptors.length === 0) return; // nothing valid: no-op
      // Ahead of the frequency cap and of any listener: a visitor on the other
      // device class is not someone this popup is being withheld from, they are
      // someone it was never for, so nothing is armed and no re-show window is
      // opened on them.
      if (!targetsThisDevice()) return;
      // Same reasoning one page over: a visitor on a page the popup was never
      // for is not someone it is being withheld from, so nothing is armed and
      // no re-show window is opened on them.
      if (!targetsThisPage()) return;
      // Same reasoning again: campaign, referrer, new/returning, schedule and
      // country are read unconditionally (the landing capture below is what
      // lets a later page's popup see what page one arrived with), and a
      // visitor none of them target is not someone this popup is withheld
      // from either.
      if (
        !targetsTargeting(
          options.targeting,
          landingContext(sessionMarkStorage()),
          storage.read(VISITOR_SEEN_KEY) !== null,
          options.visitorCountry,
        )
      ) {
        return;
      }
      // A cap withholds an uninvited popup; it does not switch a button off. So
      // a capped form (frequency window, `maxShows`, or an explicit decline)
      // still arms its user-initiated triggers and drops every other one: the
      // "talk to us" button keeps working, and no uninvited trigger is smuggled
      // in beside it (task 4.1). With no such trigger this is today's early
      // return, before consent is consulted and before anything is attached.
      const armable = isSuppressed()
        ? descriptors.filter(isUserInitiated)
        : descriptors;
      if (armable.length === 0) return;
      // Consult the posture before a single listener is attached, which is the
      // CLAUDE.md §13 rule and the reason this sits above the loop rather than
      // beside the beacon. Deliberately below the two guards: a visitor nothing
      // is armed for is not someone a consent question has been asked about.
      //
      // The answer does not decide whether to arm. Decision 0.2 gates the
      // counting layer and never the modal, so a denial makes this popup open
      // exactly as it does today and count nothing (4.3). Reading it here, once
      // and early, is what gives every later counter the same answer and gives
      // the dashboard a sentence to show.
      //
      // The "seen before" mark for `targeting.visitor` is written here too,
      // and only here: a persistent (localStorage) identifier is exactly the
      // kind of thing a "no" has to mean "don't record", so it goes down only
      // on the branch that already knows the answer was "yes" (task 3.1).
      if (consultConsent().allowed) {
        if (storage.read(VISITOR_SEEN_KEY) === null) {
          storage.write(VISITOR_SEEN_KEY, "1");
        }
      }
      armed = true;
      // Each top-level descriptor gets the shared one-shot `fire`; the first to
      // fire wins (OR). A composite `all` descriptor gates its own `fire` call
      // behind every sub-trigger latching (AND), via armAll.
      for (const desc of armable) {
        const byRequest = isUserInitiated(desc);
        armDescriptor(desc, (reason) => fire(onFire, byRequest, reason));
        if (fired) break; // a descriptor fired synchronously (e.g. already scrolled)
      }
    },
    markShown(): void {
      // Nothing is written under a resolved denial (docs/consent-management_plan.md
      // task 5.6): not the count, not the timestamp, not the session mark below.
      if (!mayRecord()) return;
      // Read-then-increment against the persisted record rather than an
      // instance counter, so the total carries across page loads the way
      // `shownAt` does. `writeState` merges onto a fresh read, so a second
      // instance on the same page cannot clobber the count.
      //
      // Called once per open, including the second and third press of a click
      // popup, which since 4.1 reopens on every press. Plan task 4.2: a
      // user-initiated open counts here on the same terms as an uninvited one,
      // because the visitor has genuinely seen the form and an exit popup
      // offering that same form a minute later would be a repeat. What 4.1
      // changed is only that the press is never *withheld* by the number this
      // writes: `fire` skips `isSuppressed()` on the user path, so a visitor
      // can spend `maxShows` on their own presses and still open it again.
      // The analytics counterpart, and why three presses are not read as three
      // impressions, is beside the `popup_shown` emits in kiroku-embed.ts and
      // PopupCTA.tsx. Pinned by tests/popup-reopen-emits-a-view.test.tsx.
      writeState({
        shownAt: Date.now(),
        shownCount: toShownCount(readState().shownCount) + 1,
      });
      // Session mark for the frequencyDays === 0 case (task 1.2). Written
      // unconditionally on the frequencyDays check rather than guessing at a
      // save-time re-check: harmless to set when a non-zero window already
      // governs re-show, since isSuppressed() never reads sessionKey then.
      if (frequencyDays === 0 && sessionKey) {
        sessionMarkStorage().write(sessionKey, "1");
      }
    },
    markConverted(): void {
      if (!mayRecord()) return;
      writeState({ convertedAt: Date.now() });
    },
    markDeclined(): void {
      if (!mayRecord()) return;
      writeState({ declinedAt: Date.now() });
    },
    disarm(): void {
      // The watch goes with the listeners: a host that has torn the popup down
      // is not counting anything either, so an interval left running on their
      // page would be reading a signal nobody asks about.
      stopConsentWatch();
      cleanup();
    },
    consent(): TrackingConsentResolution {
      return consultConsent();
    },
    revokeTracking,
  };
}
